// C-S5A2-2 «Что значит ваш результат» — Stage 4 §7.
// Source: docs/specs/sprint-5a.2/stage-4-frontend.md §5 (state machine) + §7 (полный код).
//
// Single-marker insight block. Дёргает useBiomarkerInsight(code) — code приходит prop'ом
// от BiomarkerDetail (parent уже знает code из route params).
// Заголовок «Что значит ваш результат» живёт ВНУТРИ компонента (R1 M3 Phase 0) — чтобы
// terminal hide мог вернуть null без оставления orphan header'a.

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useBiomarkerInsight } from '@/api/hooks/biomarkerInsights';
import type {
  BiomarkerInsightReady,
  BiomarkerInsightResponse,
} from '@/api/types';

// Stage 1 §4.6 + Cfg-S5A2-1 + R1 L/M — hardcoded 60_000 ms для Stage 4.
const POLL_TIMEOUT_MS = 60_000;

export type InsightBlockState =
  | 'loading'
  | 'loaded'
  | 'refreshing'
  | 'unavailable'
  | 'no_range'
  | 'hide_disabled'
  | 'hide_no_uploads'
  | 'terminal_no_verified_rule';

type DeriveInsightStateInput = {
  isLoading: boolean;
  isError: boolean;
  data: BiomarkerInsightResponse | undefined;
  hasStash: boolean;
  isTimedOut: boolean;
};

export function deriveInsightState(input: DeriveInsightStateInput): InsightBlockState {
  const { isLoading, isError, data, hasStash, isTimedOut } = input;

  if (data?.status === 'disabled') return 'hide_disabled';
  if (data?.status === 'no_uploads') return 'hide_no_uploads';
  if (data?.status === 'no_verified_rule') return 'terminal_no_verified_rule';

  // no_range — terminal 200 с populated payload (D4-S5A2-9 — render как loaded static).
  if (data?.status === 'no_range') return 'no_range';

  if (isTimedOut && hasStash) return 'loaded';
  if (isError && hasStash) return 'loaded';

  if (isTimedOut) return 'unavailable';
  if (isError) return 'unavailable';

  if (data?.status === 'ready') return 'loaded';
  if (data?.status === 'generating' && hasStash) return 'refreshing';
  if (data?.status === 'generating' && !hasStash) return 'loading';

  if (isLoading) return 'loading';

  return 'loading';
}

export function renderInsightText(
  state: InsightBlockState,
  data: BiomarkerInsightResponse | undefined,
  stash: BiomarkerInsightReady | null,
): string {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.insight.text;
    if (stash !== null) return stash.insight.text;
  }
  if (state === 'refreshing' && stash !== null) return stash.insight.text;
  if (state === 'no_range' && data?.status === 'no_range') return data.payload.text;
  return '';
}

export function pickInsightDisclaimer(
  state: InsightBlockState,
  data: BiomarkerInsightResponse | undefined,
  stash: BiomarkerInsightReady | null,
): string {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.disclaimer;
    if (stash !== null) return stash.disclaimer;
  }
  if (state === 'refreshing' && stash !== null) return stash.disclaimer;
  return '';
}

export function pickInsightPermanentDisclaimer(
  state: InsightBlockState,
  data: BiomarkerInsightResponse | undefined,
  stash: BiomarkerInsightReady | null,
): string {
  if (state === 'loaded' || state === 'refreshing') {
    if (data && 'permanent_disclaimer' in data) return data.permanent_disclaimer;
    if (stash !== null) return stash.permanent_disclaimer;
  }
  if (state === 'no_range' && data && 'permanent_disclaimer' in data) {
    return data.permanent_disclaimer;
  }
  if (state === 'terminal_no_verified_rule' && data && 'permanent_disclaimer' in data) {
    return data.permanent_disclaimer;
  }
  if (state === 'unavailable' && stash !== null) return stash.permanent_disclaimer;
  return '';
}

export function BiomarkerInsightBlock({
  code,
  headless = false,
}: {
  code: string;
  /** Если true — компонент рендерит только контент без своей шапки/обёртки section.
   *  Используется когда parent (например, BiomarkerDetail) уже даёт amber-card обёртку
   *  с собственным заголовком. */
  headless?: boolean;
}) {
  const generatingStartedAt = useRef<number | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const query = useBiomarkerInsight(code, { enabled: !isTimedOut });

  const lastReadyRef = useRef<BiomarkerInsightReady | null>(null);
  useEffect(() => {
    if (query.data?.status === 'ready') {
      lastReadyRef.current = query.data;
    }
  }, [query.data]);

  useEffect(() => {
    const status = query.data?.status;
    if (status === 'ready' || query.isError || (status !== undefined && status !== 'generating')) {
      generatingStartedAt.current = null;
      setIsTimedOut(false);
      return;
    }
    if (status === 'generating') {
      if (generatingStartedAt.current === null) {
        generatingStartedAt.current = Date.now();
      }
      const elapsed = Date.now() - generatingStartedAt.current;
      const remaining = Math.max(0, POLL_TIMEOUT_MS - elapsed);
      if (remaining === 0) {
        setIsTimedOut(true);
        return;
      }
      const t = setTimeout(() => setIsTimedOut(true), remaining);
      return () => clearTimeout(t);
    }
  }, [query.data?.status, query.isError]);

  const state = deriveInsightState({
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data,
    hasStash: lastReadyRef.current !== null,
    isTimedOut,
  });

  if (state === 'hide_disabled' || state === 'hide_no_uploads') {
    return null;
  }

  const text = renderInsightText(state, query.data, lastReadyRef.current);
  const blockDisclaimer = pickInsightDisclaimer(state, query.data, lastReadyRef.current);
  const permanentDisclaimer = pickInsightPermanentDisclaimer(state, query.data, lastReadyRef.current);

  if (state === 'terminal_no_verified_rule') {
    return permanentDisclaimer ? (
      <p className="text-hl-small leading-relaxed text-muted-foreground/80">
        {permanentDisclaimer}
      </p>
    ) : null;
  }

  const Container = headless ? 'div' : 'section';

  return (
    <Container
      aria-live="polite"
      aria-busy={state === 'loading' || state === 'refreshing'}
    >
      {!headless && (
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">Что значит ваш результат</h2>
      )}

      {state === 'refreshing' && (
        <div
          className="mb-3 flex items-center gap-2 rounded-md bg-primary/5 px-3 py-2 text-hl-small text-primary-text"
          role="status"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          <span>Обновляем подсказки на основе новых анализов…</span>
        </div>
      )}

      {state === 'loading' && (
        <div className="space-y-2">
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-[85%] animate-pulse rounded bg-muted" />
          <div className="h-4 w-[60%] animate-pulse rounded bg-muted" />
        </div>
      )}

      {(state === 'loaded' || state === 'refreshing' || state === 'no_range') && (
        <>
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{text}</p>
          {blockDisclaimer && (
            <p className="mt-4 text-hl-small leading-relaxed text-muted-foreground">
              {blockDisclaimer}
            </p>
          )}
          {permanentDisclaimer && (
            <p className="mt-2 text-hl-small leading-relaxed text-muted-foreground/80">
              {permanentDisclaimer}
            </p>
          )}
        </>
      )}

      {state === 'unavailable' && (
        <>
          <div className="py-2 text-center text-sm text-muted-foreground">
            <p>Подсказки временно недоступны.</p>
            <p className="mt-1">Откройте экран позже — мы их добавим.</p>
          </div>
          {permanentDisclaimer && (
            <p className="mt-2 text-hl-small leading-relaxed text-muted-foreground/80">
              {permanentDisclaimer}
            </p>
          )}
        </>
      )}
    </Container>
  );
}
